/**
 * SkillsRadar — Dashboard widget that shows the learner where their hours
 * are landing across skill areas (course tags). Uses a lightweight SVG
 * radar chart — no external dep. Pulls from /api/lms/my-skills-radar.
 */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Radar, TrendingUp, Loader2 } from 'lucide-react';

const polar = (cx, cy, r, angleDeg) => {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const SkillsRadar = () => {
  const { API, user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const token = window.localStorage.getItem('token');
        const res = await axios.get(`${API}/lms/my-skills-radar`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        setData(res.data);
      } catch (_) { /* silent */ } finally { setLoading(false); }
    })();
  }, [API, user]);

  // Take top 6 skills for a readable chart
  const skills = useMemo(() => (data?.skills || []).slice(0, 6), [data]);
  const maxHours = useMemo(() => Math.max(0.1, ...skills.map((s) => s.hours || 0)), [skills]);

  // Chart geometry
  const CX = 150, CY = 150, R = 110;
  const grid = [0.25, 0.5, 0.75, 1];

  const points = skills.map((s, i) => {
    const angle = (i / Math.max(3, skills.length)) * 360;
    const r = R * ((s.hours || 0) / maxHours);
    return { s, angle, coords: polar(CX, CY, r, angle) };
  });

  const gridPoints = (fr) => {
    return skills.map((_, i) => {
      const angle = (i / Math.max(3, skills.length)) * 360;
      return polar(CX, CY, R * fr, angle).join(',');
    }).join(' ');
  };

  const labelPoints = skills.map((s, i) => {
    const angle = (i / Math.max(3, skills.length)) * 360;
    const [x, y] = polar(CX, CY, R + 22, angle);
    return { s, x, y, anchor: (angle > 30 && angle < 150) ? 'start' : (angle > 210 && angle < 330) ? 'end' : 'middle' };
  });

  if (loading) {
    return (
      <section className="pro-card p-6 sm:p-7 bg-card border border-border rounded-3xl shadow-lg" data-testid="skills-radar">
        <div className="flex items-center gap-2 text-foreground/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading skills…
        </div>
      </section>
    );
  }

  const isEmpty = skills.length === 0;
  const needsMoreForRadar = skills.length > 0 && skills.length < 3;

  return (
    <section className="pro-card p-6 sm:p-7 bg-card border border-border rounded-3xl shadow-lg" data-testid="skills-radar">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="eyebrow text-primary">Where your hours land</div>
          <h2 className="font-heading text-xl sm:text-2xl font-extrabold text-foreground tracking-tight mt-2">
            {isEmpty ? 'No skill data yet.' : (
              <>
                <span className="text-primary">{data?.total_hours ?? 0}h</span> across{' '}
                <span className="text-primary">{data?.courses_touched ?? 0}</span> courses
              </>
            )}
          </h2>
        </div>
        <TrendingUp className="w-6 h-6 text-primary flex-shrink-0" />
      </header>

      {isEmpty && (
        <p className="text-sm text-foreground/70" data-testid="skills-radar-empty">
          Start any course and the radar will fill in as you spend verified time on lessons.
        </p>
      )}

      {needsMoreForRadar && (
        <div className="text-sm text-foreground/70" data-testid="skills-radar-partial">
          You&apos;ve only touched {skills.length === 1 ? 'one' : 'two'} skill area so far — the radar chart activates once you&apos;ve logged verified time across at least three tags. Below is a preview:
          <ul className="mt-4 space-y-2" data-testid="skills-radar-list">
            {skills.map((s) => (
              <li key={s.tag} className="flex items-center gap-3 py-1" data-testid={`skills-row-${s.tag.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-extrabold text-foreground tracking-tight text-sm truncate">{s.tag}</div>
                  <div className="h-1.5 rounded-full bg-primary/10 overflow-hidden mt-1">
                    <div className="h-full bg-primary" style={{ width: `${(s.hours / maxHours) * 100}%` }} />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-heading font-extrabold text-foreground tracking-tight text-sm">{s.hours}h</div>
                  <div className="text-[10px] text-foreground/50 font-bold">{s.lessons_completed} lessons</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isEmpty && !needsMoreForRadar && (
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Chart */}
          <svg viewBox="0 0 300 300" className="w-full max-w-[280px] h-auto flex-shrink-0" aria-label="Skills radar chart">
            {/* Concentric grid */}
            {grid.map((fr) => (
              <polygon
                key={fr}
                points={gridPoints(fr)}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.15"
                className="text-foreground"
              />
            ))}
            {/* Spokes */}
            {skills.map((_, i) => {
              const angle = (i / Math.max(3, skills.length)) * 360;
              const [x, y] = polar(CX, CY, R, angle);
              return (
                <line
                  key={i}
                  x1={CX} y1={CY} x2={x} y2={y}
                  stroke="currentColor"
                  strokeOpacity="0.1"
                  className="text-foreground"
                />
              );
            })}
            {/* Data polygon */}
            {points.length > 2 && (
              <polygon
                points={points.map((p) => p.coords.join(',')).join(' ')}
                fill="hsl(var(--primary))"
                fillOpacity="0.25"
                stroke="hsl(var(--primary))"
                strokeWidth="2"
                data-testid="skills-radar-shape"
              />
            )}
            {/* Data points */}
            {points.map((p, i) => (
              <circle
                key={i}
                cx={p.coords[0]}
                cy={p.coords[1]}
                r="4"
                fill="hsl(var(--primary))"
              />
            ))}
            {/* Labels */}
            {labelPoints.map(({ s, x, y, anchor }, i) => (
              <g key={i}>
                <text
                  x={x} y={y}
                  textAnchor={anchor}
                  className="fill-foreground font-heading font-extrabold"
                  style={{ fontSize: '11px' }}
                >
                  {s.tag.length > 14 ? `${s.tag.slice(0, 13)}…` : s.tag}
                </text>
                <text
                  x={x} y={y + 12}
                  textAnchor={anchor}
                  className="fill-primary font-heading font-black"
                  style={{ fontSize: '10px' }}
                >
                  {s.hours}h
                </text>
              </g>
            ))}
          </svg>

          {/* Table */}
          <div className="flex-1 w-full">
            <ul className="space-y-2" data-testid="skills-radar-list">
              {skills.map((s) => (
                <li
                  key={s.tag}
                  className="flex items-center gap-3 py-1"
                  data-testid={`skills-row-${s.tag.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-extrabold text-foreground tracking-tight text-sm truncate">{s.tag}</div>
                    <div className="h-1.5 rounded-full bg-primary/10 overflow-hidden mt-1">
                      <div
                        className="h-full bg-primary"
                        style={{ width: `${(s.hours / maxHours) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-heading font-extrabold text-foreground tracking-tight text-sm">{s.hours}h</div>
                    <div className="text-[10px] text-foreground/50 font-bold">{s.lessons_completed} lessons</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
};

export default SkillsRadar;
